import React, { useState } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import { CiTimer } from "react-icons/ci";
import './timings-history.css';
import TimingLayout from '../../components/TimingLayout';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { FaClock, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import ReasonModal from './ReasonModal';

const rowsPerPage = 10;

export default function TimingsHistory() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [reasonType, setReasonType] = useState('');

  const validateForm = () => {
    const errors = {};
    if (!startDate) errors.startDate = 'Start Date is required';
    if (!endDate) errors.endDate = 'End Date is required';
    if (startDate && endDate && startDate > endDate) {
      errors.endDate = 'End date cannot be before start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerateReport = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setShowTable(true);
    setCurrentPage(1);
    setData([]);

    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        alert("User not logged in");
        return;
      }

      const startISO = `${startDate}T00:00:00.000Z`;
      const endISO = `${endDate}T23:59:59.999Z`;

      const response = await axios.get(
        `https://backend.ems.hiradentallab.com/api/attendance/filter-attendance`,
        {
          params: { startDate: startISO, endDate: endISO, userId }
        }
      );

      if (response.data.success) {
        setData(response.data.data || []);
      } else {
        setData([]);
        alert(response.data.message || "No records found");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(error.response?.data?.message || "Failed to fetch data. Please try again.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getRowClassName = (item) => {
    return (item.lateMinutes > 0 || item.earlyCloseMinutes > 0) ? 'late-row' : 'on-time-row';
  };

  const exportToExcel = () => {
    const wsData = [
      ["SR", "Date", "CheckIn", "CheckOut", "Worked Hours", "Late (min)", "Early Close (min)", "Over-Time (min)"],
      ...data.map((item, i) => [
        i + 1,
        formatDate(item.date),
        formatTime(item.checkIn),
        formatTime(item.checkOut),
        item.workedHours || '-',
        item.lateMinutes || '-',
        item.earlyCloseMinutes || '-',
        item.extraMinutes || '-'
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timings");
    XLSX.writeFile(wb, "Timings_History.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Timings History Report", 14, 15);
    doc.autoTable({
      startY: 25,
      head: [["SR", "Date", "CheckIn", "CheckOut", "Worked", "Late", "Early", "OT"]],
      body: data.map((item, i) => [
        i + 1,
        formatDate(item.date),
        formatTime(item.checkIn),
        formatTime(item.checkOut),
        item.workedHours || '-',
        item.lateMinutes || '-',
        item.earlyCloseMinutes || '-',
        item.extraMinutes || '-'
      ]),
      theme: "grid",
      styles: { fontSize: 10 }
    });
    doc.save("Timings_History.pdf");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Fixed: Removed duplicate "pages;" line
  const pageRange = () => {
    const totalPages = Math.ceil(data.length / rowsPerPage);
    const pages = [];
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + 2);
    if (end - start < 2) start = Math.max(1, end - 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleReasonClick = (record, type) => {
    setCurrentRecord(record);
    setReasonType(type);
    setShowReasonModal(true);
  };

  const handleSubmitReason = async (reason) => {
    try {
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const department = localStorage.getItem('department');

      await axios.post(
        `https://backend.ems.hiradentallab.com/api/attendance/${currentRecord._id}/reason`,
        { type: reasonType, reason, userId, userName, department }
      );

      setData(prev => prev.map(item =>
        item._id === currentRecord._id
          ? { ...item, [`${reasonType}Reason`]: reason, [`${reasonType}Approved`]: false }
          : item
      ));
      setShowReasonModal(false);
      alert("Reason submitted successfully!");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to submit reason");
    }
  };

  const renderReasonButton = (record, type) => {
    const field = type === 'late' ? 'lateMinutes' : type === 'earlyClose' ? 'earlyCloseMinutes' : 'extraMinutes';
    const reason = record[`${type}Reason`];
    const approved = record[`${type}Approved`];

    if (!record[field] || record[field] <= 0) return null;

    if (!reason) return <Button variant="link" size="sm" onClick={() => handleReasonClick(record, type)} title="Add Reason"><FaClock style={{ color: '#007bff' }} /></Button>;
    if (!approved) return <Button variant="link" size="sm" onClick={() => handleReasonClick(record, type)} title="Pending"><FaHourglassHalf style={{ color: '#ffc107' }} /></Button>;
    return <Button variant="link" size="sm" onClick={() => handleReasonClick(record, type)} title="Approved"><FaCheckCircle style={{ color: '#28a745' }} /></Button>;
  };

  const currentData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.ceil(data.length / rowsPerPage);

  return (
    <div className='container-fluid'>
      <TimingLayout>
        <div>
          <h1 className='timings-history-title'>
            <CiTimer className='timings-history-icon' /> Timing History
          </h1>
          <hr />

          <Form className='timings-history-form mb-4'>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Start Date <span className='required'>*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    isInvalid={!!formErrors.startDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>End Date <span className='required'>*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    isInvalid={!!formErrors.endDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.endDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Button
              variant="primary"
              onClick={handleGenerateReport}
              disabled={loading}
              className="mt-3"
            >
              {loading ? "Generating Report..." : "Generate Report"}
            </Button>
          </Form>

          {showTable && (
            <div className='timings-history-table-container'>
              {data.length > 0 && (
                <div className="mb-3">
                  <Button variant="success" size="sm" onClick={exportToExcel} className="me-2">Export Excel</Button>
                  <Button variant="danger" size="sm" onClick={exportToPDF}>Export PDF</Button>
                </div>
              )}

              <div className="table-responsive">
                <table className='table table-striped table-hover'>
                  <thead>
                    <tr className="time-history-table-header">
                      <th>SR</th>
                      <th>Date</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Worked</th>
                      <th>Late</th>
                      <th>Early</th>
                      <th>OT</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="9" className="text-center">Loading...</td></tr>
                    ) : data.length === 0 ? (
                      <tr><td colSpan="9" className="text-center text-muted">No records found for selected dates</td></tr>
                    ) : (
                      currentData.map((item, index) => (
                        <tr key={item._id} className={getRowClassName(item)}>
                          <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                          <td>{formatDate(item.date)}</td>
                          <td>{formatTime(item.checkIn)}</td>
                          <td>{formatTime(item.checkOut)}</td>
                          <td>{item.workedHours || '-'}</td>
                          <td>{item.lateMinutes || '-'} {renderReasonButton(item, 'late')}</td>
                          <td>{item.earlyCloseMinutes || '-'} {renderReasonButton(item, 'earlyClose')}</td>
                          <td>{item.extraMinutes || '-'} {renderReasonButton(item, 'overtime')}</td>
                          <td>-</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <nav className="d-flex justify-content-center mt-3">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                    </li>
                    {pageRange().map(num => (
                      <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(num)}>{num}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          )}

          <ReasonModal
            show={showReasonModal}
            onHide={() => setShowReasonModal(false)}
            onSubmit={handleSubmitReason}
            type={reasonType}
            record={currentRecord}
          />
        </div>
      </TimingLayout>
    </div>
  );
}